import { Formik } from 'formik';
import { useState } from 'react';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import styled from 'styled-components';

import { Space } from '@goatapp/web-content-components';
import { CheckBalanceForm } from 'gift-cards/components/checkBalance/CheckBalanceForm';
import { checkBalanceInitialFormValues } from 'gift-cards/enums/constants';
import { transformSpaces } from 'gift-cards/utils/transformSpaces';
import { checkBalanceValidationSchema } from 'gift-cards/utils/validations';
import Footer from 'layout/footer/components/Footer';
import { Image } from 'shared/components/Image';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';

const TP = 'giftCards.components.checkBalance.CheckBalance';

export const CheckBalance = () => {
  const { isMobile } = useWindowSize();
  const [space, setSpace] = useState(null);
  const [balance, setBalance] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async ({ email, code }) => {
    const strippedGiftCardCode = code.split('-').join('');
    setErrorMessage(null);

    try {
      const { availableBalanceCents, giftCardId } = await requestService({
        url: '/web-api/v1/gift-cards/validate-with-email',
        method: 'post',
        data: {
          giftCardCode: strippedGiftCardCode,
          recipientEmail: email,
        },
      });

      const { spaceId } = await requestService({
        url: `/web-api/v1/gift-cards/${giftCardId as string}`,
        method: 'get',
      });

      const giftCardSpace = await requestService({
        url: `/web-api/v1/spaces/${spaceId as string}`,
        method: 'get',
      });

      if (availableBalanceCents === 0) {
        setErrorMessage(t(`${TP}.noRemainingBalance`, 'This card has no remaining balance'));
      }

      setBalance(availableBalanceCents);
      const subsection = transformSpaces(giftCardSpace.data);
      setSpace(subsection);

      sendTrackingEvent('GC_CHECK_BALANCE_TAP', {
        balance_text:
          availableBalanceCents === 0
            ? t(`${TP}.noRemainingBalance`, 'This card has no remaining balance')
            : null,
        is_valid: true,
      });
    } catch (error) {
      setErrorMessage(error.message);

      sendTrackingEvent('GC_CHECK_BALANCE_TAP', {
        is_valid: false,
      });
    }
  };

  return (
    <CheckBalance.Wrapper>
      <CheckBalance.ContentWrapper>
        <CheckBalance.Items>
          <CheckBalance.SpaceWrapper>
            {space ? (
              <Space space={space} maxHeight={isMobile ? 168 : 440} />
            ) : (
              <Image
                alt={t(`${TP}.goatGiftCardAlt`, 'GOAT Gift Card')}
                noSrcModification
                src="https://cms-cdn.goat.com/3000/c30f92ab8837-e0c8-de11-b857-02552b01.gif"
                sizes="294px"
              />
            )}
          </CheckBalance.SpaceWrapper>
          <Formik
            initialValues={checkBalanceInitialFormValues}
            onSubmit={handleSubmit}
            validationSchema={checkBalanceValidationSchema(t)}
          >
            <CheckBalanceForm balance={balance} errorMessage={errorMessage} />
          </Formik>
        </CheckBalance.Items>
      </CheckBalance.ContentWrapper>
      <Footer />
    </CheckBalance.Wrapper>
  );
};

CheckBalance.Wrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: calc(100vh - 90px);
    display: flex;
    flex-direction: column;

    > footer {
      margin: 0;
      padding: 0px 32px 24px;
    }
  }
`;

CheckBalance.ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: calc(100vh - 144px);
    align-items: flex-start;
  }
`;

CheckBalance.Items = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: column;
    align-items: flex-start;
    margin: 7px 16px 0px 16px;
    width: 100%;
    gap: 12px;
  }
`;

CheckBalance.SpaceWrapper = styled.div`
  max-height: 750px;
  height: 440px;
  max-width: 501px;
  width: 294px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 168px;
    width: 111px;
  }
`;
